/**
 * @file AvitoOrdersPage.tsx
 * @description Список заказов Avito-Доставки (phase14.0).
 * UX: toolbar с кнопками «Обновить» + «Синхронизировать с Avito» + empty-state.
 * Mobile-first.
 */

import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { shopAvito, type AvitoOrderDto } from "../../../lib/api-shop-avito";

const AVITO_STATUS_LABELS: Record<string, string> = {
  on_confirmation: "Ожидает подтверждения",
  ready_to_ship: "К отгрузке",
  in_transit: "В пути",
  delivered: "Доставлен",
  closed: "Закрыт",
  canceled: "Отменён",
  on_return: "На возврате",
  in_dispute: "Спор",
};

export function AvitoOrdersPage() {
  const notify = useNotify();
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<AvitoOrderDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await shopAvito.orders({ page: 1, perPage: 100 });
      setItems(r.items);
      setTotal(r.meta.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить заказы");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const r = await shopAvito.triggerSync("orders");
      if (r.status === "success") {
        notify.success("Синхронизация заказов завершена");
        await load();
      } else {
        notify.error("Синхронизация завершена с ошибкой — см. журнал");
      }
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось запустить синхру");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-on-surface-variant">
          Всего заказов в кэше: <strong className="text-on-surface">{total}</strong>
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading || syncing}>
            <RefreshCw className="size-4" />
            Обновить
          </Button>
          <Button onClick={syncNow} disabled={syncing} size="sm">
            {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Синхронизировать с Avito
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader2 className="size-5 animate-spin mx-auto my-16 text-on-surface-variant" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body className="text-center py-10 space-y-3">
            <p className="text-base font-medium text-on-surface">Заказов с Avito нет</p>
            <p className="text-sm text-on-surface-variant max-w-md mx-auto">
              Заказы появятся после синхронизации с Avito API. Обычно cron делает это каждые
              30 минут автоматически.
            </p>
            <Button onClick={syncNow} disabled={syncing}>
              {syncing ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Синхронизировать сейчас
            </Button>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {items.map((it) => (
              <OrderCard key={it.avitoOrderId} order={it} slug={slug} />
            ))}
          </div>
          <EditorialCard className="hidden md:block">
            <EditorialCard.Body className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-on-surface-variant border-b border-outline-variant">
                    <tr>
                      <th className="px-4 py-3">Номер</th>
                      <th className="px-4 py-3">Avito ID</th>
                      <th className="px-4 py-3">Avito-статус</th>
                      <th className="px-4 py-3">Внутр. статус</th>
                      <th className="px-4 py-3 text-right">Сумма</th>
                      <th className="px-4 py-3">Создан</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.avitoOrderId} className="border-b border-outline-variant last:border-0">
                        <td className="px-4 py-3 font-medium">
                          <Link
                            to={`/shop/${slug}/orders/${it.orderId}`}
                            className="text-primary hover:underline"
                          >
                            {it.orderNumber}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">{it.marketplaceId}</td>
                        <td className="px-4 py-3">
                          <Badge variant="default">
                            {AVITO_STATUS_LABELS[it.avitoStatus] ?? it.avitoStatus}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{it.orderStatus}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {new Intl.NumberFormat("ru-RU", {
                            style: "currency",
                            currency: "RUB",
                          }).format(Number(it.totalGross))}
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">
                          {new Date(it.createdAt).toLocaleDateString("ru-RU")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </EditorialCard.Body>
          </EditorialCard>
        </>
      )}
    </div>
  );
}

function OrderCard({ order, slug }: { order: AvitoOrderDto; slug: string | undefined }) {
  return (
    <EditorialCard>
      <EditorialCard.Body className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/shop/${slug}/orders/${order.orderId}`}
            className="font-medium text-primary hover:underline"
          >
            {order.orderNumber}
          </Link>
          <Badge variant="default">
            {AVITO_STATUS_LABELS[order.avitoStatus] ?? order.avitoStatus}
          </Badge>
        </div>
        <div className="text-sm flex items-center justify-between">
          <span className="text-on-surface-variant">Avito ID {order.marketplaceId}</span>
          <span className="font-medium">
            {new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }).format(
              Number(order.totalGross),
            )}
          </span>
        </div>
        <div className="text-xs text-on-surface-variant">
          {new Date(order.createdAt).toLocaleDateString("ru-RU")} · Внутр. {order.orderStatus}
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
